import React, { useState, useEffect } from 'react';
import {
  Settings, Layout, Shield, Eye, EyeOff,
  GripVertical, Save, RotateCcw, Bell, User, Monitor,
  Check, X, Globe
} from 'lucide-react';
import AuditTrail from './AuditTrail';
import { supabase } from '../lib/supabase';
import './SettingsPage.css';

// Default home widget configuration
const DEFAULT_WIDGETS = [
  { id: 'diligence',   label: 'Information Requests',        visible: true },
  { id: 'missing',     label: 'Missing Information Requests', visible: true },
  { id: 'documents',   label: 'Recent Documents',            visible: true },
  { id: 'activity',    label: 'Recent Activity',             visible: true },
];

const STORAGE_KEY = 'dealnexa_home_widgets';
const NOTIF_KEY   = 'dealnexa_notifications';
const APPEAR_KEY  = 'dealnexa_appearance';

function loadWidgets() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const merged = DEFAULT_WIDGETS.map(dw => {
        const s = parsed.find(sw => sw.id === dw.id);
        return s ? { ...dw, ...s } : dw;
      });
      return merged;
    }
  } catch (_) {}
  return DEFAULT_WIDGETS;
}

function saveWidgets(widgets) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets)); } catch (_) {}
}

const DEFAULT_NOTIFS = {
  documentUploaded:    true,
  dealStatusChanged:   true,
  teamMemberActivity:  false,
  diligenceUpdates:    true,
  weeklyDigest:        false,
  benchmarkOptIn:      false,
};

function loadNotifs() {
  try {
    const saved = localStorage.getItem(NOTIF_KEY);
    if (saved) return { ...DEFAULT_NOTIFS, ...JSON.parse(saved) };
  } catch (_) {}
  return { ...DEFAULT_NOTIFS };
}

const DEFAULT_APPEAR = {
  compactSidebar:  false,
  denseTableView:  false,
  showTips:        true,
};

function loadAppear() {
  try {
    const saved = localStorage.getItem(APPEAR_KEY);
    if (saved) return { ...DEFAULT_APPEAR, ...JSON.parse(saved) };
  } catch (_) {}
  return { ...DEFAULT_APPEAR };
}

// ── Toggle component ───────────────────────────────────────────────────────
function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      className={`settings-toggle ${checked ? 'on' : 'off'}`}
      onClick={() => onChange(!checked)}
      aria-checked={checked}
      role="switch"
    >
      <span className="settings-toggle-thumb" />
    </button>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
const SettingsPage = ({ currentUser, orgData, onNavigate }) => {
  const [activeTab, setActiveTab] = useState('general');

  // Widget state
  const [widgets, setWidgets]         = useState(loadWidgets);
  const [widgetsSaved, setWidgetsSaved] = useState(false);
  const [dragIdx, setDragIdx]         = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Notification state
  const [notifs, setNotifs]           = useState(loadNotifs);
  const [notifSaved, setNotifSaved]   = useState(false);

  // Appearance state
  const [appear, setAppear]           = useState(loadAppear);
  const [appearSaved, setAppearSaved] = useState(false);

  // Account / password change state
  const [pwCurrent, setPwCurrent]     = useState('');
  const [pwNew, setPwNew]             = useState('');
  const [pwConfirm, setPwConfirm]     = useState('');
  const [showPwCurrent, setShowPwCurrent] = useState(false);
  const [showPwNew, setShowPwNew]     = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);
  const [pwLoading, setPwLoading]     = useState(false);
  const [pwError, setPwError]         = useState('');
  const [pwSuccess, setPwSuccess]     = useState('');

  const isAdmin = currentUser?.role === 'admin';

  // ── Widget helpers ─────────────────────────────────────────────────────
  const toggleWidget = (id) => {
    setWidgets(prev => prev.map(w => w.id === id ? { ...w, visible: !w.visible } : w));
    setWidgetsSaved(false);
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver  = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop      = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    const updated = [...widgets];
    const [moved] = updated.splice(dragIdx, 1);
    updated.splice(idx, 0, moved);
    setWidgets(updated);
    setDragIdx(null); setDragOverIdx(null); setWidgetsSaved(false);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const handleSaveWidgets = () => {
    saveWidgets(widgets);
    setWidgetsSaved(true);
    setTimeout(() => setWidgetsSaved(false), 2500);
    window.dispatchEvent(new Event('dealnexa:widgets-updated'));
  };

  const handleResetWidgets = () => {
    setWidgets(DEFAULT_WIDGETS);
    saveWidgets(DEFAULT_WIDGETS);
    window.dispatchEvent(new Event('dealnexa:widgets-updated'));
  };

  // ── Notification helpers ───────────────────────────────────────────────
  const handleSaveNotifs = () => {
    try { localStorage.setItem(NOTIF_KEY, JSON.stringify(notifs)); } catch (_) {}
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2500);
  };

  // ── Appearance helpers ─────────────────────────────────────────────────
  const handleSaveAppear = () => {
    try { localStorage.setItem(APPEAR_KEY, JSON.stringify(appear)); } catch (_) {}
    setAppearSaved(true);
    setTimeout(() => setAppearSaved(false), 2500);
    window.dispatchEvent(new CustomEvent('dealnexa:appearance-updated', { detail: appear }));
  };

  // ── Password change ────────────────────────────────────────────────────
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');

    if (pwNew.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('New passwords do not match.');
      return;
    }

    setPwLoading(true);
    try {
      // Re-authenticate by signing in with the current password first
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: currentUser.email,
        password: pwCurrent,
      });
      if (signInErr) throw new Error('Current password is incorrect.');

      const { error: updateErr } = await supabase.auth.updateUser({ password: pwNew });
      if (updateErr) throw updateErr;

      setPwSuccess('Password updated successfully.');
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (err) {
      setPwError(err.message || 'Failed to update password.');
    } finally {
      setPwLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-title-row">
          <Settings size={22} />
          <h1>Settings</h1>
        </div>
        {(orgData?.name || currentUser?.orgName) && <p className="settings-org-label">{orgData?.name || currentUser?.orgName}</p>}
      </div>

      <div className="settings-layout">
        {/* Sidebar nav */}
        <nav className="settings-nav">
          <button className={`settings-nav-item ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>
            <Settings size={16} /> General
          </button>
          <button className={`settings-nav-item ${activeTab === 'account' ? 'active' : ''}`} onClick={() => setActiveTab('account')}>
            <User size={16} /> Account
          </button>
          <button className={`settings-nav-item ${activeTab === 'notifications' ? 'active' : ''}`} onClick={() => setActiveTab('notifications')}>
            <Bell size={16} /> Notifications
          </button>
          <button className={`settings-nav-item ${activeTab === 'appearance' ? 'active' : ''}`} onClick={() => setActiveTab('appearance')}>
            <Monitor size={16} /> Appearance
          </button>
          <button className={`settings-nav-item ${activeTab === 'customize' ? 'active' : ''}`} onClick={() => setActiveTab('customize')}>
            <Layout size={16} /> Home Layout
          </button>
          {isAdmin && (
            <button className={`settings-nav-item ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')}>
              <Shield size={16} /> Audit Trail
            </button>
          )}
        </nav>

        {/* Content */}
        <div className="settings-content">

          {/* ── General ── */}
          {activeTab === 'general' && (
            <div className="settings-section">
              <h2>General</h2>
              <p className="settings-section-desc">Platform information and account details.</p>

              <div className="settings-info-grid">
                <div className="settings-info-card">
                  <div className="settings-info-label">Organization</div>
                  <div className="settings-info-value">{orgData?.name || currentUser?.orgName || '—'}</div>
                </div>
                <div className="settings-info-card">
                  <div className="settings-info-label">Organization Type</div>
                  <div className="settings-info-value">{orgData?.type?.replace(/_/g, ' ') || currentUser?.orgType?.replace(/_/g, ' ') || '—'}</div>
                </div>
                <div className="settings-info-card">
                  <div className="settings-info-label">Your Role</div>
                  <div className="settings-info-value" style={{ textTransform: 'capitalize' }}>{currentUser?.role || '—'}</div>
                </div>
                <div className="settings-info-card">
                  <div className="settings-info-label">Your Email</div>
                  <div className="settings-info-value">{currentUser?.email || '—'}</div>
                </div>
              </div>

              <div className="settings-feature-list">
                <h3>Platform Features</h3>
                <ul>
                  <li>Deal pipeline management with Kanban and list views</li>
                  <li>AI-powered document analysis and due diligence</li>
                  <li>Organization-wide contact management</li>
                  <li>Team collaboration with role-based permissions</li>
                  <li>Document upload, annotation, and library</li>
                  <li>Synergy analysis and deal comparison tools</li>
                  {isAdmin && <li>Audit trail — track all platform actions (Settings → Audit Trail)</li>}
                </ul>
              </div>
            </div>
          )}

          {/* ── Account ── */}
          {activeTab === 'account' && (
            <div className="settings-section">
              <h2>Account</h2>
              <p className="settings-section-desc">Manage your personal account details and security.</p>

              <div className="settings-info-grid" style={{ marginBottom: '2rem' }}>
                <div className="settings-info-card">
                  <div className="settings-info-label">Display Name</div>
                  <div className="settings-info-value">{currentUser?.name || '—'}</div>
                </div>
                <div className="settings-info-card">
                  <div className="settings-info-label">Email Address</div>
                  <div className="settings-info-value">{currentUser?.email || '—'}</div>
                </div>
                <div className="settings-info-card">
                  <div className="settings-info-label">Role</div>
                  <div className="settings-info-value" style={{ textTransform: 'capitalize' }}>{currentUser?.role || '—'}</div>
                </div>
              </div>

              <div className="settings-card">
                <h3 className="settings-card-title">Change Password</h3>
                <p className="settings-section-desc">Your new password must be at least 8 characters.</p>

                {pwError   && <div className="settings-alert settings-alert-error">{pwError}</div>}
                {pwSuccess && <div className="settings-alert settings-alert-success">{pwSuccess}</div>}

                <form onSubmit={handleChangePassword} className="settings-pw-form">
                  <div className="settings-pw-field">
                    <label>Current Password</label>
                    <div className="settings-pw-input-wrap">
                      <input
                        type={showPwCurrent ? 'text' : 'password'}
                        value={pwCurrent}
                        onChange={(e) => setPwCurrent(e.target.value)}
                        required
                        placeholder="Enter current password"
                      />
                      <button type="button" className="settings-pw-eye" onClick={() => setShowPwCurrent(!showPwCurrent)} aria-label="Toggle visibility">
                        {showPwCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="settings-pw-field">
                    <label>New Password</label>
                    <div className="settings-pw-input-wrap">
                      <input
                        type={showPwNew ? 'text' : 'password'}
                        value={pwNew}
                        onChange={(e) => setPwNew(e.target.value)}
                        required
                        placeholder="Enter new password"
                      />
                      <button type="button" className="settings-pw-eye" onClick={() => setShowPwNew(!showPwNew)} aria-label="Toggle visibility">
                        {showPwNew ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="settings-pw-field">
                    <label>Confirm New Password</label>
                    <div className="settings-pw-input-wrap">
                      <input
                        type={showPwConfirm ? 'text' : 'password'}
                        value={pwConfirm}
                        onChange={(e) => setPwConfirm(e.target.value)}
                        required
                        placeholder="Confirm new password"
                      />
                      <button type="button" className="settings-pw-eye" onClick={() => setShowPwConfirm(!showPwConfirm)} aria-label="Toggle visibility">
                        {showPwConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" className="btn-settings-primary" disabled={pwLoading}>
                    {pwLoading ? 'Updating...' : 'Update Password'}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* ── Notifications ── */}
          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h2>Notifications</h2>
              <p className="settings-section-desc">Control which email notifications you receive from DealNexa.</p>

              <div className="settings-card">
                <h3 className="settings-card-title">Email Notifications</h3>

                <div className="settings-toggle-list">
                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <span className="settings-toggle-label">Document Uploaded</span>
                      <span className="settings-toggle-desc">Get notified when a new document is added to a deal.</span>
                    </div>
                    <Toggle checked={notifs.documentUploaded} onChange={(v) => setNotifs(p => ({ ...p, documentUploaded: v }))} />
                  </div>

                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <span className="settings-toggle-label">Deal Status Changed</span>
                      <span className="settings-toggle-desc">Receive alerts when a deal moves between pipeline stages.</span>
                    </div>
                    <Toggle checked={notifs.dealStatusChanged} onChange={(v) => setNotifs(p => ({ ...p, dealStatusChanged: v }))} />
                  </div>

                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <span className="settings-toggle-label">Team Member Activity</span>
                      <span className="settings-toggle-desc">Know when teammates add notes, annotations, or findings.</span>
                    </div>
                    <Toggle checked={notifs.teamMemberActivity} onChange={(v) => setNotifs(p => ({ ...p, teamMemberActivity: v }))} />
                  </div>

                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <span className="settings-toggle-label">Due Diligence Updates</span>
                      <span className="settings-toggle-desc">Get notified when AI findings or diligence items are updated.</span>
                    </div>
                    <Toggle checked={notifs.diligenceUpdates} onChange={(v) => setNotifs(p => ({ ...p, diligenceUpdates: v }))} />
                  </div>

                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <span className="settings-toggle-label">Weekly Digest</span>
                      <span className="settings-toggle-desc">A summary email of your organization's activity each week.</span>
                    </div>
                    <Toggle checked={notifs.weeklyDigest} onChange={(v) => setNotifs(p => ({ ...p, weeklyDigest: v }))} />
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h3 className="settings-card-title"><Globe size={14} style={{ display:'inline', marginRight:6 }} />Platform Benchmarks</h3>
                <div className="settings-toggle-list">
                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <span className="settings-toggle-label">Contribute to Benchmarks</span>
                      <span className="settings-toggle-desc">
                        Share anonymized, aggregated deal metrics (win rate, deal durations, type breakdown) with the DealNexa platform.
                        No deal names, values, or identifying information is ever shared. In return, your team gains access to cross-industry benchmarks in Deal Intelligence.
                      </span>
                    </div>
                    <Toggle checked={notifs.benchmarkOptIn} onChange={(v) => setNotifs(p => ({ ...p, benchmarkOptIn: v }))} />
                  </div>
                </div>
                {notifs.benchmarkOptIn && (
                  <p className="settings-note" style={{ marginTop: '0.75rem', color: '#16a34a' }}>
                    ✓ You are contributing to platform benchmarks. Thank you — this helps the whole DealNexa community.
                  </p>
                )}
              </div>

              <div className="widget-actions" style={{ marginTop: '1.5rem' }}>
                <button className="btn-settings-primary" onClick={handleSaveNotifs}>
                  {notifSaved ? <><Check size={14} /> Saved!</> : <><Save size={14} /> Save Preferences</>}
                </button>
              </div>
              <p className="settings-note">Preferences are saved locally and will be synced with your profile in a future update.</p>
            </div>
          )}

          {/* ── Appearance ── */}
          {activeTab === 'appearance' && (
            <div className="settings-section">
              <h2>Appearance</h2>
              <p className="settings-section-desc">Customize how the platform looks and feels for you.</p>

              <div className="settings-card">
                <h3 className="settings-card-title">Display Options</h3>

                <div className="settings-toggle-list">
                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <span className="settings-toggle-label">Compact Sidebar</span>
                      <span className="settings-toggle-desc">Collapse sidebar labels to show only icons, giving more space to content.</span>
                    </div>
                    <Toggle checked={appear.compactSidebar} onChange={(v) => setAppear(p => ({ ...p, compactSidebar: v }))} />
                  </div>

                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <span className="settings-toggle-label">Dense Table View</span>
                      <span className="settings-toggle-desc">Show more rows by reducing row height in tables and lists.</span>
                    </div>
                    <Toggle checked={appear.denseTableView} onChange={(v) => setAppear(p => ({ ...p, denseTableView: v }))} />
                  </div>

                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <span className="settings-toggle-label">Show In-App Tips</span>
                      <span className="settings-toggle-desc">Display helpful tooltips and onboarding hints throughout the platform.</span>
                    </div>
                    <Toggle checked={appear.showTips} onChange={(v) => setAppear(p => ({ ...p, showTips: v }))} />
                  </div>
                </div>
              </div>

              <div className="widget-actions" style={{ marginTop: '1.5rem' }}>
                <button className="btn-settings-primary" onClick={handleSaveAppear}>
                  {appearSaved ? <><Check size={14} /> Saved!</> : <><Save size={14} /> Save Preferences</>}
                </button>
              </div>
              <p className="settings-note">Appearance settings are saved to your browser per device.</p>
            </div>
          )}

          {/* ── Home Layout (previously "Customize") ── */}
          {activeTab === 'customize' && (
            <div className="settings-section">
              <h2>Home Layout</h2>
              <p className="settings-section-desc">
                Choose which widgets appear on your home dashboard and reorder them by dragging.
              </p>

              <div className="widget-list">
                {widgets.map((w, idx) => (
                  <div
                    key={w.id}
                    className={`widget-row ${dragOverIdx === idx ? 'drag-over' : ''} ${!w.visible ? 'widget-hidden' : ''}`}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDrop={(e) => handleDrop(e, idx)}
                    onDragEnd={handleDragEnd}
                  >
                    <span className="widget-drag-handle" title="Drag to reorder">
                      <GripVertical size={16} />
                    </span>
                    <span className={`widget-label ${!w.visible ? 'muted' : ''}`}>{w.label}</span>
                    <button
                      className={`widget-toggle-btn ${w.visible ? 'visible' : 'hidden'}`}
                      onClick={() => toggleWidget(w.id)}
                      title={w.visible ? 'Hide widget' : 'Show widget'}
                    >
                      {w.visible ? <><Eye size={14} /> Visible</> : <><EyeOff size={14} /> Hidden</>}
                    </button>
                  </div>
                ))}
              </div>

              <div className="widget-actions">
                <button className="btn-settings-secondary" onClick={handleResetWidgets}>
                  <RotateCcw size={14} /> Reset to Default
                </button>
                <button className="btn-settings-primary" onClick={handleSaveWidgets}>
                  {widgetsSaved ? <><Save size={14} /> Saved!</> : <><Save size={14} /> Save Layout</>}
                </button>
              </div>

              <p className="settings-note">
                Changes are saved to your browser. Each user can set their own home layout.
              </p>
            </div>
          )}

          {/* ── Audit Trail ── */}
          {activeTab === 'audit' && isAdmin && (
            <div className="settings-section">
              <h2>Audit Trail</h2>
              <p className="settings-section-desc">
                Track all actions performed within your organization. Visible to admins only.
              </p>
              <AuditTrail currentUser={currentUser} orgMembers={[]} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { loadWidgets, DEFAULT_WIDGETS, STORAGE_KEY };
export default SettingsPage;
